const memoryStorage = new Map();

const DEPRECATED_LOCAL_STORAGE_KEYS = [
  'jlastore.home.image-cache.v1',
  'jlastore.game-details.image-cache.v1',
];

let deprecatedKeysCleared = false;

const getStorageArea = (type = 'local') => {
  if (typeof window === 'undefined') return null;

  try {
    return type === 'session' ? window.sessionStorage : window.localStorage;
  } catch (error) {
    return null;
  }
};

const getMemoryKey = (type, key) => `${type}:${key}`;

const clearDeprecatedLocalStorageKeys = () => {
  if (deprecatedKeysCleared) return;
  deprecatedKeysCleared = true;

  const storage = getStorageArea('local');

  DEPRECATED_LOCAL_STORAGE_KEYS.forEach((key) => {
    memoryStorage.delete(getMemoryKey('local', key));

    try {
      storage?.removeItem(key);
    } catch (error) {
      // Ignore storage cleanup failures.
    }
  });
};

clearDeprecatedLocalStorageKeys();

export const safeGetItem = (key, fallback = null, { type = 'local' } = {}) => {
  const storage = getStorageArea(type);

  try {
    const value = storage?.getItem(key);
    if (value !== null && value !== undefined) return value;
  } catch (error) {
    // Fall through to memory storage.
  }

  return memoryStorage.get(getMemoryKey(type, key)) ?? fallback;
};

export const safeSetItem = (key, value, { type = 'local', pruneOnQuota = true } = {}) => {
  const stringValue = String(value);
  const storage = getStorageArea(type);

  memoryStorage.set(getMemoryKey(type, key), stringValue);

  if (!storage) return false;

  try {
    storage.setItem(key, stringValue);
    return true;
  } catch (error) {
    if (!pruneOnQuota || type !== 'local') return false;

    try {
      DEPRECATED_LOCAL_STORAGE_KEYS
        .filter((cacheKey) => cacheKey !== key)
        .forEach((cacheKey) => storage.removeItem(cacheKey));
      storage.setItem(key, stringValue);
      return true;
    } catch (retryError) {
      return false;
    }
  }
};

export const safeRemoveItem = (key, { type = 'local' } = {}) => {
  memoryStorage.delete(getMemoryKey(type, key));

  try {
    getStorageArea(type)?.removeItem(key);
  } catch (error) {
    // Ignore storage failures.
  }
};

export const safeJsonGet = (key, fallback = null, options = {}) => {
  const rawValue = safeGetItem(key, null, options);
  if (!rawValue) return fallback;

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    safeRemoveItem(key, options);
    return fallback;
  }
};

export const safeJsonSet = (key, value, options = {}) =>
  safeSetItem(key, JSON.stringify(value), options);
